/**
 * Лента примерок: результаты пар «пин × платье» группами по генерации.
 *
 * Одна лента на три экрана — `/generate`, карточка пина, карточка платья;
 * различается только фильтр. Пока что-то в очереди или в работе, лента
 * опрашивает сервис сама. Плитка — по образцу `GenTile` ленты кадров, но
 * пропорции берутся из размера (примерка бывает 2:3), а звезда и пересъёмка
 * идут в ручки `/api/fittings/variants/*`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowUpFromLine, Star } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { Empty, ErrorLine, Loading } from "@/components/trends/ui";
import {
  fittingKeys,
  fittingPath,
  FITTINGS_PREFIX,
  isRouterModel,
  loadFittings,
  pickFittingVariant,
  reshootFittingVariant,
  type FeedFilter,
  type FitGen,
  type FittingsResult,
} from "@/lib/fitting";
import { mediaUrl } from "@/lib/media";
import { resultPath } from "@/lib/results";
import { groupInOrder, newKey, usd } from "@/lib/project";
import { enhanceNote, STAGE_HINT, STAGE_LABEL, stageOf } from "@/lib/verdict";

/**
 * Ключи пересъёмок — на весь модуль: ключ живёт до УСПЕШНОГО ответа, повтор
 * после сорвавшегося запроса уходит с тем же ключом, и сервис не ставит вторую
 * оплаченную задачу. Замок `reshootInFlight` держит двойной клик.
 */
const reshootKeys = new Map<number, string>();
const reshootInFlight = new Set<number>();

/** Загруженное и улучшенное: ни «Повторить с этими параметрами», ни «переснять в финале» — сервер откажет. */
const notRedone = (kind: FitGen["kind"]) => kind === "upload" || kind === "enhance";

function reshootKey(variantId: number): string {
  let key = reshootKeys.get(variantId);
  if (!key) {
    key = newKey();
    reshootKeys.set(variantId, key);
  }
  return key;
}

export function FittingFeed({
  filter,
  seed,
  emptyText,
}: {
  filter: FeedFilter;
  seed?: FittingsResult;
  emptyText: ReactNode;
}) {
  const client = useQueryClient();
  const feed = useQuery({
    queryKey: fittingKeys.feed(filter),
    queryFn: () => loadFittings(filter),
    initialData: seed,
    staleTime: 5_000,
    refetchInterval: (q) => ((q.state.data?.pending ?? 0) > 0 ? 3_000 : false),
  });

  // 200 с текстом в `error` — отказ сервиса (бюджет, нет файла): показываем под лентой.
  const [lastError, setLastError] = useState("");
  // Итог автозапуска улучшения после последней отметки (пусто — не запускалось).
  const [enhanceText, setEnhanceText] = useState<string | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: FITTINGS_PREFIX });

  // Звезда ПЕРЕКЛЮЧАЕТ: замок в ref, потому что `isPending` обновится только
  // после перерисовки, а второй клик приходит раньше.
  const picking = useRef(false);
  const pick = useMutation({
    mutationFn: (variantId: number) => pickFittingVariant(variantId),
    onSettled: () => {
      picking.current = false;
    },
    onSuccess: (got) => {
      setLastError(got.error ?? "");
      setEnhanceText(enhanceNote(got.enhance));
      return refresh();
    },
  });
  const reshoot = useMutation({
    mutationFn: (variantId: number) => reshootFittingVariant(variantId, reshootKey(variantId)),
    onSuccess: (got, variantId) => {
      setLastError(got.error ?? "");
      if (got.error) return; // ключ не сбрасываем: повтор пойдёт с ним же
      reshootKeys.delete(variantId);
      return refresh();
    },
    onSettled: (_data, _error, variantId) => {
      reshootInFlight.delete(variantId);
    },
  });

  const onPick = (variantId: number) => {
    if (picking.current) return;
    picking.current = true;
    pick.mutate(variantId);
  };
  const onReshoot = (variantId: number) => {
    if (reshootInFlight.has(variantId)) return;
    reshootInFlight.add(variantId);
    reshoot.mutate(variantId);
  };

  const data = feed.data;
  if (feed.isError) return <ErrorLine error={feed.error} />;
  if (!data) return <Loading />;
  const groups = groupInOrder(data.gens, (g) => g.generation_id);

  return (
    <div className="grid gap-3 p-3" data-annot="лента примерок">
      <ErrorLine error={pick.error ?? reshoot.error} />
      {data.error ? <ErrorLine error={new Error(data.error)} /> : null}
      {lastError ? <ErrorLine error={new Error(lastError)} /> : null}
      {enhanceText ? <div className="text-[12px] text-fg-muted">{enhanceText}</div> : null}
      {groups.length === 0 ? (
        <Empty>{emptyText}</Empty>
      ) : (
        <>
          <div className="text-[11.5px] text-fg-faint">
            новые сверху
            {data.pending > 0 ? ` · в очереди и в работе: ${data.pending}` : ""}
          </div>
          {groups.map(([generationId, rows]) => (
            <Group
              key={generationId}
              rows={rows}
              onPick={onPick}
              onReshoot={onReshoot}
              picking={pick.isPending ? (pick.variables ?? null) : null}
              reshooting={reshoot.isPending ? (reshoot.variables ?? null) : null}
            />
          ))}
        </>
      )}
    </div>
  );
}

function Group({
  rows,
  onPick,
  onReshoot,
  picking,
  reshooting,
}: {
  rows: FitGen[];
  onPick: (variantId: number) => void;
  onReshoot: (variantId: number) => void;
  picking: number | null;
  reshooting: number | null;
}) {
  const head = rows[0]!;
  const done = head.generation_cost_actual !== null && head.generation_cost_actual !== undefined;
  const cost = done ? usd(head.generation_cost_actual) : `~${usd(head.generation_cost_estimate)}`;
  const dress = head.dress;
  const ownDress = head.own_dress_id;
  const ref = head.reference;
  const refGone = head.reference_item_id === null && ref !== null;
  const manual = head.kind === "manual";
  const scenes = head.pin_id !== null ? { pins: [head.pin_id] } : { refs: head.reference_item_id !== null ? [head.reference_item_id] : [] };

  return (
    <div className="grid gap-1.5" data-annot="группа генерации">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-fg-muted">
        <span className="tnum">
          {(isRouterModel(head.generation_model) ? null : (head.preset ?? head.quality)) ?? ""}
          {isRouterModel(head.generation_model) ? "" : " · "}
          {cost}
        </span>
        {head.diff.map((d) => (
          <span key={d} className="rounded-[var(--radius-ui)] border border-border bg-bg px-1.5 text-[11.5px]">
            {d}
          </span>
        ))}
        {head.pin ? (
          <Link to={`/pins/${head.pin.id}`} className="text-accent-text hover:underline">
            пин #{head.pin.id}
          </Link>
        ) : null}
        {ref && !refGone ? (
          <Link to={`/lib/ref/${ref.id}`} className="text-accent-text hover:underline">
            референс «{ref.library_name}» #{ref.id}
          </Link>
        ) : null}
        {refGone && ref ? <span>референс удалён (был: {ref.library_name} #{ref.id})</span> : null}
        {manual ? (
          <span className="rounded-[var(--radius-ui)] border border-border bg-bg px-1.5 text-[11.5px]">вручную</span>
        ) : null}
        {dress?.uploaded ? (
          <span>Платье из загрузки</span>
        ) : ownDress !== null ? (
          <Link to={`/photos/${ownDress}`} className="text-accent-text hover:underline">
            {dress?.name ?? `Платье #${dress?.id ?? ownDress}`}
          </Link>
        ) : dress ? (
          <span>{dress.name ?? `Платье #${dress.id}`} (папка удалена)</span>
        ) : null}
        {head.extras.length > 0 ? (
          <span className="inline-flex items-center gap-1" title="вставленные картинки">
            {head.extras.map((x) => (
              <img
                key={x.asset_id}
                src={mediaUrl(x.path)}
                alt=""
                title={x.comment || "без комментария"}
                className="size-7 rounded-[3px] border border-border object-cover"
              />
            ))}
          </span>
        ) : null}
        {head.note ? (
          <span className="line-clamp-1 max-w-[40ch]" title={head.note}>
            {head.note}
          </span>
        ) : null}
        {head.kind === "shot" || notRedone(head.kind) ? null : refGone ? (
          <span aria-disabled="true" className="text-fg-faint">
            Повторить с этими параметрами
          </span>
        ) : (
          <Link to={fittingPath(scenes, ownDress ?? undefined, head.generation_id)} className="text-accent-text hover:underline">
            Повторить с этими параметрами
          </Link>
        )}
      </div>
      {head.prompt ? (
        <details className="text-[11.5px] text-fg-muted">
          <summary className="cursor-pointer">промпт</summary>
          <pre className="mt-1 max-h-60 overflow-auto rounded-[var(--radius-ui)] bg-bg-elevated p-2 whitespace-pre-wrap">{head.prompt}</pre>
        </details>
      ) : null}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {rows.map((g) => (
          <Tile
            key={`${g.generation_id}-${g.variant_id ?? "x"}`}
            g={g}
            onPick={onPick}
            onReshoot={onReshoot}
            picking={g.variant_id !== null && picking === g.variant_id}
            reshooting={g.variant_id !== null && reshooting === g.variant_id}
          />
        ))}
      </div>
    </div>
  );
}

function Tile({
  g,
  onPick,
  onReshoot,
  picking,
  reshooting,
}: {
  g: FitGen;
  onPick: (variantId: number) => void;
  onReshoot: (variantId: number) => void;
  picking: boolean;
  reshooting: boolean;
}) {
  const isPicked = g.pick === "picked";
  const waiting = g.status === "queued" || g.status === "running";
  const vid = g.variant_id;
  const router = isRouterModel(g.generation_model);
  // у OpenRouter размера WxH нет — пропорция плитки из `aspect_ratio` («2:3» → «2 / 3»)
  const ratio = g.params?.aspect_ratio ?? null;
  const aspect = { aspectRatio: g.size ? g.size.replace("x", " / ") : ratio ? ratio.replace(":", " / ") : "9 / 16" };
  const shape = router ? (ratio ?? g.params?.resolution ?? "") : g.size;

  return (
    <div
      className={clsx(
        "relative w-[110px] flex-none rounded-[var(--radius-ui)] border bg-bg p-1",
        isPicked ? "border-accent ring-1 ring-accent" : "border-border",
      )}
      title={[g.generation_model, g.quality, shape].filter(Boolean).join(" · ")}
    >
      {vid !== null && g.asset_path ? (
        <>
          <Link to={resultPath(vid)}>
            <img
              src={mediaUrl(g.asset_path)}
              alt={`${g.short_model} ${g.quality}`}
              loading="lazy"
              style={aspect}
              className="w-full rounded-[4px] object-cover"
            />
          </Link>
          <button
            type="button"
            title={isPicked ? `снять «${STAGE_LABEL[stageOf(g.kind)]}»` : STAGE_HINT[stageOf(g.kind)]}
            disabled={picking}
            onClick={() => onPick(vid)}
            className={clsx(
              "absolute top-1.5 right-1.5 grid size-6 place-items-center rounded-full bg-bg/80 backdrop-blur hover:bg-bg",
              isPicked ? "text-accent" : "text-fg-muted",
            )}
          >
            <Star size={13} fill={isPicked ? "currentColor" : "none"} />
          </button>
          {g.kind === "manual" || notRedone(g.kind) || router ? null : (
            <button
              type="button"
              title="переснять в финале"
              disabled={reshooting}
              onClick={() => onReshoot(vid)}
              className="absolute top-1.5 left-1.5 grid size-6 place-items-center rounded-full bg-bg/80 text-fg-muted backdrop-blur hover:bg-bg hover:text-accent disabled:opacity-50"
            >
              <ArrowUpFromLine size={12} />
            </button>
          )}
        </>
      ) : waiting ? (
        <div
          style={aspect}
          className="grid w-full place-items-center rounded-[4px] bg-warn-bg text-[18px] text-warn"
          title={g.status === "queued" ? "в очереди" : "генерируется"}
        >
          <span className={g.status === "running" ? "animate-pulse" : ""}>{g.status === "queued" ? "…" : "◐"}</span>
        </div>
      ) : g.status === "failed" ? (
        <div
          style={aspect}
          className="grid w-full place-items-center rounded-[4px] bg-err-bg p-1 text-center text-err"
          title={g.generation_error ?? "ошибка"}
        >
          <div>
            <div className="text-[18px] font-semibold">!</div>
            <div className="line-clamp-4 text-[9.5px] leading-tight break-words">{g.generation_error ?? "ошибка"}</div>
          </div>
        </div>
      ) : (
        <div
          style={aspect}
          className="grid w-full place-items-center rounded-[4px] bg-bg-elevated text-[11px] text-fg-faint"
        >
          {g.status}
        </div>
      )}
      <div className="mt-1 text-[10.5px] leading-tight text-fg-faint">
        <div className="truncate">
          {g.short_model}
          {g.quality ? ` · ${g.quality}` : ""}
        </div>
        {g.cost_each !== null ? (
          <b className="tnum font-medium text-fg-muted">{usd(g.cost_each)}</b>
        ) : waiting ? (
          <b className="tnum font-medium text-warn">~{usd(g.cost_each_estimate ?? 0)}</b>
        ) : null}
      </div>
    </div>
  );
}
